import { useEffect, useState } from 'react'
import { loadScript } from '../globalHeader/common/loadScript'
import { loginSocial, upgradeGuestSocial } from '../globalHeader/functions/ams-functions'
import { analyticsManager } from '../globalHeader/services/analytics/AnalyticsManager'
import { reportSharedError } from '../utils/errorReporting'
import { ServerErrors } from '../utils/serverErrors'
import SocialButton from './SocialButton'
import { SocialType } from './SocialLogin'

declare global {
    interface Window {
        AppleID: any
    }
}

interface IProps {
    appleClientId: string
    appleRedirectURL: string
    isGuest: boolean
    onSignUpSocial: (socialResponse: any) => void
    onLoginSuccessful: () => void
}

const AppleAuth: React.FC<IProps> = (props) => {
    const { onSignUpSocial, onLoginSuccessful, isGuest, appleClientId, appleRedirectURL } = props
    const [socialType, setSocialType] = useState(SocialType.APPLE)

    useEffect(() => {
        const src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

        loadScript(src)
            .then(() => {
                window.AppleID.auth.init({
                    clientId: appleClientId,
                    scope: 'email name',
                    redirectURI: appleRedirectURL,
                    state: 'ICC user authentication',
                    nonce: 'ICC auth',
                    usePopup: true,
                })
            })
            .catch((error) => reportSharedError('Apple SDK script load failed', error, { src }))

        return () => {
            const scriptTag = document.querySelector(`script[src="${src}"]`)
            if (scriptTag) document.body.removeChild(scriptTag)
        }
    }, [])

    const handleResponse = async () => {
        try {
            // Handle successful response.
            const response = await window.AppleID.auth.signIn()

            let data: any = null
            if (isGuest) {
                data = await upgradeGuestSocial(
                    response.authorization.code,
                    response.authorization.id_token,
                    socialType,
                )
            } else {
                data = await loginSocial(response.authorization.code, response.authorization.id_token, socialType)
            }

            if (data.error) {
                if (
                    data.error.code === ServerErrors.USERNAME_IS_MANDATORY_FOR_FIRST_LOGIN ||
                    data.error.code === ServerErrors.SOCIAL_LOGIN_MANDATORY_FIELDS_IS_MISSING
                ) {
                    onSignUpSocial({
                        ...response,
                        socialToken: data.error.details.token,
                        socialType,
                    })
                } else {
                    reportSharedError('Apple auth rejected by server', data.error, { code: data.error.code })
                }
            } else {
                analyticsManager.dispatchEvent('completeLogin', {
                    authType: 'apple',
                    userId: data.user.id,
                    userName: data.user.user_name,
                })
                onLoginSuccessful()
            }
        } catch (error) {
            // The Apple popup rejects with { error: 'popup_closed_by_user' } on a
            // plain user cancel — that's not a failure worth reporting.
            if ((error as { error?: string })?.error === 'popup_closed_by_user') return
            reportSharedError('Apple auth failed', error)
        }
    }

    return (
        <SocialButton label="Apple" onClick={handleResponse}>
            <img style={{ width: '23px', height: '23px' }} src="/assets/images/apple.svg" />
        </SocialButton>
    )
}

AppleAuth.displayName = 'AppleAuth'

export default AppleAuth
